import { FormattingService } from 'src/app/core/services/formatting.service';
import { Component, OnInit, Input } from '@angular/core';
import { PerformanceIndicator } from '../../models/performanceIndicator';
import { TranslateService } from '@ngx-translate/core';
import { Router, ActivatedRoute, UrlTree } from '@angular/router';
import { PerformanceIndicatorSettings } from '../../models/performanceIndicatorSettings';
import { LanguageService } from '../../services/language.service';
import { CurrencyHelper } from 'src/app/core/helpers/currency.helper';
import { PerformanceIndicatorEntityApiModel } from '../../models/performanceIndicatorEntityApiModel';
import { Location } from '@angular/common';
import { PeriodHelper } from '../../helpers/period.helper';
import { NewPeriodSelectorConfigurationType } from '../../enums/newPeriodSelectorConfigurationType';
import { EntityType } from '../../enums/entityType';
import { EntityService } from '../../services/entity.service';
import { TargetType } from '../../enums/targetType';
import { PerformanceInidicatorHelper } from '../../helpers/performanceInidicator.helper';
import { IconType } from '../../enums/iconType';

@Component({
    selector: 'app-performance-indicator',
    templateUrl: './performance-indicator.component.html',
    styleUrls: ['./performance-indicator.component.scss'],
    standalone: false
})
export class PerformanceIndicatorComponent implements OnInit {
  @Input()
  width: number;

  @Input()
  height: number;

  @Input()
  model: PerformanceIndicator;

  @Input()
  performanceIndicatorEntity: PerformanceIndicatorEntityApiModel

  @Input()
  showAddOrEditBudgetButton: boolean;

  @Input()
  settings: PerformanceIndicatorSettings;

  actualWidth: number;
  actualPercentage: string;
  calculatedPercent: number;
  actualValue: string;
  hasTargetValue: boolean;
  buttonName: string;
  swapColors = false;
  performanceIndicatorDescription: string;
  targetTypes = TargetType;
  iconType = IconType;

  constructor(
    private readonly translateService: TranslateService,
    private readonly router: Router,
    private readonly formattingService: FormattingService,
    private readonly languageService: LanguageService,
    private readonly activatedRoute: ActivatedRoute,
    private readonly location: Location,
    private readonly entityService: EntityService
  ) { }

  ngOnInit() {
    const currentLanguage = this.translateService.currentLang;
    if (this.languageService.getSwapColorsLanguageCodes().indexOf(currentLanguage) > -1) {
      this.swapColors = true;
    }
    if (this.model !== undefined) {
      this.hasTargetValue = false;
      if (this.model.hasTargets) {
        if (this.model.targetValue) {
          this.hasTargetValue = true;
          this.calculatedPercent = (this.model.actualValue / this.model.targetValue) * 100;
          this.actualWidth = this.calculatedPercent <= 150 ? this.calculatedPercent * 0.66 : 100;
          this.actualPercentage = Math.floor(this.calculatedPercent) + '%';
          this.model.calculatedValue = this.calculatedPercent;
          this.setEditTargetValueButtonName();
        } else {
          this.setAddTargetValueButtonName();
        }
      } else {
        this.setAddTargetValueButtonName();
      }

      if (this.model.actualValue !== null) {
        this.actualValue = this.formattingService.getDisplayFormattedNumber(
          this.model.actualValue,
          CurrencyHelper.isCurrency(this.model.characteristicType));
      }
    }

    if (this.model.resourceClassificationIdentifier) {
      if (this.translateService.instant(`ResourceDescription.${this.model.resourceClassificationIdentifier}`) !== this.model.resourceClassificationIdentifier) {
      this.performanceIndicatorDescription = this.translateService.instant(`ResourceDescription.${this.model.resourceClassificationIdentifier}`, {
        UnitSymbol: this.model.unitSymbol,
        DenominatorUnitSymbol: this.model.denominatorUnitSymbol,
        EntityType: this.entityService.getEntityTypeDescription(this.performanceIndicatorEntity.entityType),
      });
     }
   }
  }

  setAddTargetValueButtonName() {
    switch (this.model.targetType) {
      case TargetType.Budget:
        this.buttonName = this.translateService.instant('Shell.performanceIndicator.setBudget');
        break;
      case TargetType.Target:
        this.buttonName = this.translateService.instant('Shell.performanceIndicator.setTarget');
        break;
      case TargetType.None:
        this.buttonName = '';
        break;
    }
  }

  setEditTargetValueButtonName() {
    switch (this.model.targetType) {
      case TargetType.Budget:
        this.buttonName = this.translateService.instant('Shell.performanceIndicator.editBudget');
        break;
      case TargetType.Target:
        this.buttonName = this.translateService.instant('Shell.performanceIndicator.editTarget');
        break;
      case TargetType.None:
        this.buttonName = '';
        break;
    }
  }

  shouldEditTargetButtonAppear(): boolean{
    return this.showAddOrEditBudgetButton && this.model.targetType !== this.targetTypes.None && this.model.canEditTarget === true
  }

  getPerformanceBarColor() {
    return PerformanceInidicatorHelper.getPerformanceColor(this.model,this.swapColors);
  }

  showPerformanceIndicatorDetailsGraph(event: any) {
    event.stopPropagation();
    const urlTree = this.router.parseUrl(this.location.path());
    const route = this.getPIDetailsGraphRoute();
    const queryParams = {
      cropCycle: this.performanceIndicatorEntity.entityType === EntityType.CropCycle
        ? this.performanceIndicatorEntity.id
        : null,
      periodSelectorConfiguration: this.getCurrentPeriodSelectorConfiguration(urlTree),
      periodSelectorInterval: urlTree.queryParams.periodSelectorType
        ? PeriodHelper.getPeriodIntervalFromPeriodType(
          urlTree.queryParams.periodSelectorType,
          (urlTree.queryParams.startDate !== undefined || urlTree.queryParams.periodSelectorStartDate !== undefined))
        : urlTree.queryParams.periodSelectorInterval,
      periodSelectorStartDate: urlTree.queryParams.startDate ?? urlTree.queryParams.periodSelectorStartDate,
      periodSelectorEndDate: urlTree.queryParams.endDate ?? urlTree.queryParams.periodSelectorEndDate,
      periodSelectorIntervalLength: urlTree.queryParams.weekNumber ?? urlTree.queryParams.periodSelectorIntervalLength
    };
    this.router.navigate(route, { queryParams });
  }

  getCurrentPeriodSelectorConfiguration(urlTree: UrlTree): any {
    if (urlTree.queryParams.periodSelectorConfiguration) {
      return urlTree.queryParams.periodSelectorConfiguration;
    }
    // todo: remove this when adding the new period selector to all pages
    return urlTree.toString().includes('compare-crop-cycles')
      ? NewPeriodSelectorConfigurationType.CompareCropCycle
      : NewPeriodSelectorConfigurationType.OldPeriodSelector;
  }

  getPIDetailsGraphRoute() {
    const organizationId = +this.activatedRoute.snapshot.paramMap.get('organizationId');
    if (this.model.resourceClassificationIdentifier) {
      return ['manager', 'organizations', organizationId, 'performance-indicator', 'details-graph', 'entity-id',
        this.performanceIndicatorEntity.id, 'entity-type', this.performanceIndicatorEntity.entityType, this.model.resourceClassificationIdentifier];
    } else {
      return ['manager', 'organizations', organizationId, 'performance-indicator', 'details-graph', 'entity-id',
        this.performanceIndicatorEntity.id, 'entity-type', this.performanceIndicatorEntity.entityType, 'characteristic-type',
        this.model.characteristicType, 'category-type', this.model.categoryType, 'quantity-identifier', this.model.quantityIdentifier,
        'target-type', this.model.targetType];
    }
  }

  onAddEditBudget() {
    localStorage.setItem('selectedProjectionsResourceClassificationIdentifier', this.model.resourceClassificationIdentifier);
    if (this.performanceIndicatorEntity.entityType === EntityType.Site) {
      this.router.navigate([`manager/organizations/${this.settings.organizationId}/projections/sites/${this.settings.siteId}/targetDetails`]);
    }
    if (this.performanceIndicatorEntity.entityType === EntityType.CropCycle) {
      this.router.navigate([`manager/organizations/${this.settings.organizationId}/projections/sites/${this.settings.siteId}/crops/${this.performanceIndicatorEntity.id}/targetDetails`]);
    }
  }

  getTooltipIconId() {
    return `${EntityType[this.performanceIndicatorEntity.entityType]}_${this.performanceIndicatorEntity.id}_${this.model.resourceClassificationIdentifier ? this.model.resourceClassificationIdentifier : ''}_performanceIndicatorId`;
  }
}
